import React, { useState } from "react";
import { useParams } from "react-router-dom";
import classNames from "classnames";

import {
  Id,
  Identifiable,
  Folder as F,
  Document as D,
  folder as folderMock,
  Sort as S,
} from "@planzone/dto";
import {
  useMoveDocument,
  useRetrieveCurrentPlanzone,
  useRetrieveDocuments,
  useRetrieveDocumentTemplatesFolder,
  useUploadDocument,
} from "@planzone/view-models";
import { Loader } from "@common/loader";
import { Draggable, DropBox } from "@common/dragAndDrop";
import { getSelectedFolderPath } from "../utils";
import { Props } from "./types";
import { Folder } from "./folder";
import { Document } from "./document";
import { DropPreview } from "./dropPreview";
import { Header } from "./header";
import { Path } from "./path";
import { DropZone } from "./dropZone";
import { CreationMenu } from "./creationMenu";
import { Preview } from "./preview";
import { separateDocumentAndFolder } from "./utils";
import { Edit } from "./edit";
import { Placeholder } from "./placeholder";
import { Sort } from "@common/sort";
import styles from "./styles.scss";

function getSelectedFolder(
  folder: Identifiable<F>,
  id: Id,
): Identifiable<F> | null {
  return folder.uuid === id
    ? folder
    : folder.children.reduce<Identifiable<F> | null>(
        (selectedFolder, child) =>
          !!selectedFolder
            ? selectedFolder
            : child.type === "folder"
              ? getSelectedFolder(child, id)
              : selectedFolder,
        null,
      );
}

function DocumentsPage({ project, sideBar }: Props) {
  const [selectedDocument, setSelectedDocument] =
    React.useState<Identifiable<D> | null>(null);
  const [isDocumentMoved, setIsDocumentMoved] = useState(false);
  const [sort, setSort] = React.useState<S | null>(null);
  const [editedDocument, setEditedDocument] =
    React.useState<Identifiable<D> | null>(null);
  const { folder } = useRetrieveDocuments(project.uuid, "project");
  const { folderId = "" } = useParams<{ folderId: Id }>();
  const { planzone } = useRetrieveCurrentPlanzone();
  const selectedFolder = getSelectedFolder(folder, folderId) || folder;
  const { path } = React.useMemo(
    () => getSelectedFolderPath({ uuid: folderId, ...folderMock }, folder),
    [folderId],
  );
  const { isRequestPending, uploadDocument } =
    useUploadDocument(selectedFolder);
  const { moveDocument } = useMoveDocument();
  const { folder: templateFolder, retrieveDocumentTemplatesFolder } =
    useRetrieveDocumentTemplatesFolder();

  React.useEffect(() => {
    retrieveDocumentTemplatesFolder({ planzone: planzone.uuid });
  }, []);

  return (
    <div className={styles.documentsPage}>
      <div className={styles.header}>
        <CreationMenu project={project} folder={selectedFolder} />
      </div>
      <div className={styles.content}>
        {sideBar}
        <DropZone
          className={styles.main}
          drop={(file) =>
            !isRequestPending &&
            uploadDocument({
              namespace: project.uuid,
              type: "project",
              folder: selectedFolder,
              file,
            })
          }
        >
          {({ isDraggingOver }) => (
            <div className={styles.dragContent}>
              {path.length > 0 && <Path project={project} path={path} />}
              <div className={styles.documents}>
                <Header sort={sort} setSort={setSort} />
                <Sort
                  sort={sort ? sort : "ascendingName"}
                  data={selectedFolder.children}
                  extractCriteria={(children) => ({
                    name: children.name,
                    date: children.createdDate,
                    author: children.createdBy,
                  })}
                >
                  {(content) =>
                    content.length > 0 ? (
                      <ul className={styles.list}>
                        {content
                          .sort(separateDocumentAndFolder)
                          .map((child) => (
                            <li
                              key={`${sort}:${child.uuid}`}
                              className={styles.item}
                            >
                              {child.type === "folder" ? (
                                <DropBox
                                  acceptedTypes={["MOVE_DOCUMENT"]}
                                  drop={(data) => {
                                    moveDocument({
                                      document: data.payload,
                                      from: data.payload.parentUuid,
                                      to: child.uuid,
                                    });
                                  }}
                                >
                                  {({ payload }, canDrop) => (
                                    <div
                                      className={classNames({
                                        [styles.dropZone]: canDrop,
                                      })}
                                    >
                                      <Folder
                                        project={project}
                                        folder={child}
                                      />
                                    </div>
                                  )}
                                </DropBox>
                              ) : (
                                <Draggable
                                  data={child}
                                  type="MOVE_DOCUMENT"
                                  draggedClassName={styles.draggedItem}
                                  startDrag={() => setIsDocumentMoved(true)}
                                  stopDrag={() => setIsDocumentMoved(false)}
                                >
                                  <Document
                                    document={child}
                                    project={project}
                                    templateFolder={templateFolder}
                                    currentFolder={selectedFolder}
                                    select={() => setSelectedDocument(child)}
                                    edit={() => setEditedDocument(child)}
                                  />
                                </Draggable>
                              )}
                            </li>
                          ))}
                      </ul>
                    ) : (
                      <Placeholder
                        folder={{ uuid: selectedFolder.uuid, ...folderMock }}
                        project={project}
                      />
                    )
                  }
                </Sort>
              </div>
              {(isDraggingOver || isRequestPending) && !isDocumentMoved && (
                <div className={styles.dropPreview}>
                  <DropPreview />
                  {isRequestPending && (
                    <div className={styles.loader}>
                      <div>
                        <Loader />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DropZone>
      </div>
      {selectedDocument && (
        <Preview
          document={selectedDocument}
          close={() => setSelectedDocument(null)}
        />
      )}
      {editedDocument && (
        <Edit document={editedDocument} close={() => setEditedDocument(null)} />
      )}
    </div>
  );
}

export { DocumentsPage };
